// src/components/mitchRoom/revenueAnalytics/RevenueAnalytics.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  getRevenueTimeSeries,
  getRevenuePacing,
  getUpcomingPayments,
  getRevenueForecast,
  getSubscriberMetrics,
  getRecentOrders,
  getTopCustomers,
  getChurnAnalysis,
  getRevenueBreakdown,
  // Advanced analytics endpoints
  getMRRWaterfall,
  getCohortRetention,
  getAdvancedMetrics,
} from 'services/api';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Alert, AlertDescription } from 'components/ui/alert';
import { cn } from 'lib/utils';
import { formatCurrency, formatPercent } from 'utils/format';
import LoadingState from '../../shared/LoadingState';
import SummaryCards from './SummaryCards';
import RevenueChart from './RevenueChart';
import SubscriberChart from './SubscriberChart';
import BreakdownCharts from './BreakdownCharts';
import OrdersTable from './OrdersTable';
import CustomersTable from './CustomersTable';
import ChurnTable from './ChurnTable';
// Advanced analytics components
import MRRWaterfallChart from './MRRWaterfallChart';
import CohortRetentionTable from './CohortRetentionTable';
import PacingPanel from './PacingPanel';
import UpcomingPayments from './UpcomingPayments';
import MonthStrip, { SEASON_START_MONTH, seasonYearOf } from './MonthStrip';
import SeasonView from './SeasonView';
import { DEFAULT_SCENARIO } from './AssumptionsPanel';

const TAB_LABELS = ['Pacing', 'Overview', 'Orders', 'Customers', 'Churn', 'SaaS Metrics'];
const TAB = { PACING: 0, OVERVIEW: 1, ORDERS: 2, CUSTOMERS: 3, CHURN: 4, SAAS: 5 };

const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

// Daily buckets are only readable over a few months; default to day for
// short ranges, month for long ones.
function defaultGranularity(start, end) {
  if (!start || !end) return 'month';
  const days = Math.round((new Date(end) - new Date(start)) / 86400000);
  if (days <= 62) return 'day';
  if (days <= 400) return 'week';
  return 'month';
}

const PRODUCT_OPTIONS = [
  { value: 'all', label: 'All Products' },
  { value: 'monthly', label: 'Monthly Only' },
  { value: 'monthly_preseason', label: 'Monthly + Pre-Season' },
  { value: 'preseason', label: 'Pre-Season Only' },
  { value: 'annual', label: 'Annual / Season Pass' },
];

const COMPARE_OPTIONS = [
  { value: 'none', label: 'No comparison' },
  { value: 'previous_year', label: 'vs Previous year' },
  { value: 'previous_period', label: 'vs Previous period' },
];

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// The season runs 1 Jul – 30 Jun; offset 0 = current season, -1 = last season
function seasonRange(offset = 0) {
  const now = new Date();
  const startYear = seasonYearOf(now.getFullYear(), now.getMonth() + 1) + offset;
  const mm = String(SEASON_START_MONTH).padStart(2, '0');
  const end = new Date(startYear + 1, SEASON_START_MONTH - 1, 0); // last day of the month before the next season
  return { start: `${startYear}-${mm}-01`, end: fmtDate(end) };
}

function lastNMonthsRange(n) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - (n - 1), 1);
  return { start: fmtDate(start), end: fmtDate(now) };
}

const DATE_PRESETS = [
  { label: 'All time', range: () => ({ start: '', end: '' }) },
  { label: 'Last 3 months', range: () => lastNMonthsRange(3) },
  { label: 'Last 12 months', range: () => lastNMonthsRange(12) },
  { label: 'This season', range: () => seasonRange(0) },
  { label: 'Last season', range: () => seasonRange(-1) },
];

// Shifted date range for the comparison series
function comparisonRange(start, end, mode) {
  if (!start || !end || !mode || mode === 'none') return null;
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (mode === 'previous_year') {
    const s2 = new Date(s);
    s2.setFullYear(s2.getFullYear() - 1);
    const e2 = new Date(e);
    e2.setFullYear(e2.getFullYear() - 1);
    return { start: fmtDate(s2), end: fmtDate(e2), label: 'Prev year' };
  }
  const dayMs = 86400000;
  const lenDays = Math.round((e - s) / dayMs) + 1;
  return {
    start: fmtDate(new Date(s.getTime() - lenDays * dayMs)),
    end: fmtDate(new Date(s.getTime() - dayMs)),
    label: 'Prev period',
  };
}

function SaasMetricCard({ label, value, accent, valueClassName }) {
  return (
    <div
      className="rounded-token-large border border-borderGrey border-l-4 bg-white p-4 text-center"
      style={{ borderLeftColor: accent }}
    >
      <p className="text-token-sm text-muted-foreground">{label}</p>
      <p className={cn('text-token-2xl font-semibold', valueClassName)}>{value}</p>
    </div>
  );
}

export default function RevenueAnalytics() {
  // State for filters (using string dates in YYYY-MM-DD format)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [compareMode, setCompareMode] = useState('none');
  // '' = follow the selected range (see defaultGranularity)
  const [granularity, setGranularity] = useState('');
  // 'auto' = deadline-aligned for August, calendar otherwise (API default)
  const [pacingAlign, setPacingAlign] = useState('auto');

  // Pacing tab: which month (or the whole season) is selected. Lives here
  // rather than being derived from the Overview end-date, so the strip can
  // show closed, current and future months of any season.
  const thisMonthKey = new Date().toISOString().slice(0, 7);
  const currentSeasonYear = seasonYearOf(new Date().getFullYear(), new Date().getMonth() + 1);
  const [seasonYear, setSeasonYear] = useState(currentSeasonYear);
  const [selectedMonth, setSelectedMonth] = useState(thisMonthKey); // 'YYYY-MM' | 'season'
  const [forecastHorizon, setForecastHorizon] = useState('season');
  const [scenario, setScenario] = useState(DEFAULT_SCENARIO);

  // State for data
  const [pacing, setPacing] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [monthForecast, setMonthForecast] = useState(null);
  const [seasonForecast, setSeasonForecast] = useState(null);
  const [pacingLoading, setPacingLoading] = useState(false);
  const [timeSeries, setTimeSeries] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [subscriberMetrics, setSubscriberMetrics] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [orders, setOrders] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [churn, setChurn] = useState(null);

  // SaaS Metrics state (advanced analytics)
  const [mrrWaterfall, setMrrWaterfall] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [advancedMetrics, setAdvancedMetrics] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(25);

  const effectiveGranularity = granularity || defaultGranularity(startDate, endDate);

  // Build filters object
  const getFilters = useCallback(() => {
    const filters = {};
    if (startDate) {
      filters.start_date = startDate;
    }
    if (endDate) {
      filters.end_date = endDate;
    }
    if (productFilter) {
      filters.product_filter = productFilter;
    }
    return filters;
  }, [startDate, endDate, productFilter]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const filters = getFilters();
    const compRange = comparisonRange(filters.start_date, filters.end_date, compareMode);

    try {
      const [timeSeriesRes, subscriberRes, breakdownRes, compTimeSeriesRes, compSubscriberRes] =
        await Promise.all([
          getRevenueTimeSeries({ ...filters, granularity: effectiveGranularity }),
          getSubscriberMetrics({
            ...filters,
            granularity: effectiveGranularity === 'week' ? 'month' : effectiveGranularity,
          }),
          getRevenueBreakdown(filters),
          compRange
            ? getRevenueTimeSeries({
                ...filters,
                granularity: effectiveGranularity,
                start_date: compRange.start,
                end_date: compRange.end,
              })
            : Promise.resolve(null),
          compRange
            ? getSubscriberMetrics({
                ...filters,
                start_date: compRange.start,
                end_date: compRange.end,
              })
            : Promise.resolve(null),
        ]);

      setTimeSeries(timeSeriesRes.data || []);
      setSubscriberMetrics(subscriberRes);
      setBreakdown(breakdownRes);
      setComparison(
        compRange
          ? {
              timeSeries: compTimeSeriesRes?.data || [],
              subscriberMetrics: compSubscriberRes,
              label: compRange.label,
              range: compRange,
            }
          : null
      );
      setLoading(false);
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError(err.message || 'Failed to load revenue analytics');
      setLoading(false);
    }
  }, [getFilters, compareMode, effectiveGranularity]);

  // Pacing is independent of the Overview date filters: it follows the
  // month strip. A month view fetches the daily pacing, the committed
  // renewals and that month's forecast; the season view fetches the
  // window forecast for the chosen horizon.
  // Only the LATEST request may write state: switching season then month
  // fires two fetches, and the slower one used to overwrite the newer.
  const pacingRequestId = useRef(0);
  const fetchPacing = useCallback(async () => {
    const reqId = ++pacingRequestId.current;
    setPacingLoading(true);
    try {
      if (selectedMonth === 'season') {
        const res = await getRevenueForecast({
          horizon: forecastHorizon,
          season: seasonYear,
          month: forecastHorizon === 'calendar_year' ? `${seasonYear}-01` : undefined,
        });
        if (reqId !== pacingRequestId.current) return;
        setSeasonForecast(res);
      } else {
        const [pacingRes, upcomingRes, forecastRes] = await Promise.all([
          getRevenuePacing({ align: pacingAlign, month: selectedMonth }),
          getUpcomingPayments({ month: selectedMonth }),
          getRevenueForecast({ horizon: 'month', month: selectedMonth }),
        ]);
        if (reqId !== pacingRequestId.current) return;
        setPacing(pacingRes);
        setUpcoming(upcomingRes);
        setMonthForecast(forecastRes);
      }
    } catch (err) {
      if (reqId !== pacingRequestId.current) return;
      console.error('Error fetching pacing:', err);
      toast.error('Failed to load pacing');
    } finally {
      if (reqId === pacingRequestId.current) setPacingLoading(false);
    }
  }, [pacingAlign, selectedMonth, seasonYear, forecastHorizon]);

  // Fetch orders (separate due to pagination)
  const fetchOrders = useCallback(async (page = 1, pageSize = ordersPageSize) => {
    const filters = { ...getFilters(), page, page_size: pageSize };
    try {
      const res = await getRecentOrders(filters);
      setOrders(res);
      setOrdersPage(page);
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Failed to load orders');
    }
  }, [getFilters, ordersPageSize]);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    const filters = getFilters();
    try {
      const res = await getTopCustomers({ ...filters, limit: 100 });
      setCustomers(res.customers || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to load customers');
    }
  }, [getFilters]);

  // Fetch churn
  const fetchChurn = useCallback(async () => {
    const filters = getFilters();
    try {
      const res = await getChurnAnalysis(filters);
      setChurn(res);
    } catch (err) {
      console.error('Error fetching churn:', err);
      toast.error('Failed to load churn analysis');
    }
  }, [getFilters]);

  // Fetch MRR Waterfall data
  const fetchMRRWaterfall = useCallback(async () => {
    const filters = getFilters();
    try {
      const res = await getMRRWaterfall(filters);
      setMrrWaterfall(res.data || []);
    } catch (err) {
      console.error('Error fetching MRR waterfall:', err);
      toast.error('Failed to load MRR waterfall');
    }
  }, [getFilters]);

  // Fetch Cohort Retention data
  const fetchCohorts = useCallback(async () => {
    const filters = getFilters();
    try {
      const res = await getCohortRetention({ ...filters, max_periods: 12 });
      setCohorts(res);
    } catch (err) {
      console.error('Error fetching cohorts:', err);
      toast.error('Failed to load cohort retention');
    }
  }, [getFilters]);

  // Fetch Advanced Metrics (NRR, LTV, ARPU)
  const fetchAdvancedMetrics = useCallback(async () => {
    const filters = getFilters();
    try {
      const res = await getAdvancedMetrics(filters);
      setAdvancedMetrics(res.metrics || res);
    } catch (err) {
      console.error('Error fetching advanced metrics:', err);
      toast.error('Failed to load advanced metrics');
    }
  }, [getFilters]);

  // Tab indices: 0 Pacing · 1 Overview · 2 Orders · 3 Customers · 4 Churn · 5 SaaS
  const handleRefresh = useCallback(() => {
    fetchData();
    if (activeTab === TAB.PACING) fetchPacing();
    if (activeTab === TAB.ORDERS) fetchOrders(1);
    if (activeTab === TAB.CUSTOMERS) fetchCustomers();
    if (activeTab === TAB.CHURN) fetchChurn();
    if (activeTab === TAB.SAAS) {
      fetchMRRWaterfall();
      fetchCohorts();
      fetchAdvancedMetrics();
    }
  }, [activeTab, fetchData, fetchPacing, fetchOrders, fetchCustomers, fetchChurn, fetchMRRWaterfall, fetchCohorts, fetchAdvancedMetrics]);

  // Keep a ref to the latest refresh fn so the debounced filter effect below
  // doesn't need activeTab in its deps (which would refetch on tab switch).
  const refreshRef = useRef(handleRefresh);
  refreshRef.current = handleRefresh;

  // Initial load + debounced auto-apply when filters change.
  // (Replaces the old redundant 'Apply Filters' button — same endpoints and
  // payloads; fetchData's identity changes exactly when the filters change.)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchData();
      // pacing's own effect below fires on mount too (same commit) — don't
      // call it here as well or the first load double-fetches
      return undefined;
    }
    const timer = setTimeout(() => refreshRef.current(), 300);
    return () => clearTimeout(timer);
    // fetchPacing intentionally omitted: it has its own effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  // Fetch pacing on mount and whenever its own inputs (alignment, month,
  // season, horizon) change
  useEffect(() => {
    fetchPacing();
  }, [fetchPacing]);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === TAB.ORDERS && !orders) {
      fetchOrders(1);
    } else if (activeTab === TAB.CUSTOMERS && customers.length === 0) {
      fetchCustomers();
    } else if (activeTab === TAB.CHURN && !churn) {
      fetchChurn();
    } else if (activeTab === TAB.SAAS && !mrrWaterfall) {
      // Fetch all SaaS metrics data in parallel
      fetchMRRWaterfall();
      fetchCohorts();
      fetchAdvancedMetrics();
    }
  }, [activeTab, orders, customers.length, churn, mrrWaterfall, fetchOrders, fetchCustomers, fetchChurn, fetchMRRWaterfall, fetchCohorts, fetchAdvancedMetrics]);

  // Keyboard shortcuts: 1-5 jump between tabs (ignored while typing)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= TAB_LABELS.length) {
        setActiveTab(n - 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleOrdersPageSizeChange = (size) => {
    setOrdersPageSize(size);
    fetchOrders(1, size);
  };

  if (loading && timeSeries.length === 0) {
    return <LoadingState message="Loading Revenue Analytics..." />;
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>Error: {error}</AlertDescription>
        </Alert>
        <Button variant="outline" size="sm" className="mt-3" onClick={handleRefresh}>
          Retry
        </Button>
      </div>
    );
  }

  const nrrPositive = advancedMetrics && (advancedMetrics.nrr || 0) >= 100;

  return (
    <div className="w-full min-w-0">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-token-xl font-semibold">Revenue Analytics</h2>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={cn(loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Filters — auto-apply (debounced), no Apply button needed */}
      <div className="mb-4 flex flex-col gap-3 rounded-token-large border border-borderGrey bg-white p-4">
        {/* Quick range presets */}
        <div className="flex flex-wrap items-center gap-1.5">
          {DATE_PRESETS.map((preset) => {
            const r = preset.range();
            const active = startDate === r.start && endDate === r.end;
            return (
              <Button
                key={preset.label}
                variant={active ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setStartDate(r.start);
                  setEndDate(r.end);
                }}
              >
                {preset.label}
              </Button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="revenue-start-date">Start Date</Label>
            <Input
              id="revenue-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 w-[160px]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="revenue-end-date">End Date</Label>
            <Input
              id="revenue-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 w-[160px]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Product</Label>
            <Select
              value={productFilter || 'all'}
              onValueChange={(v) => setProductFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="h-9 w-[210px]">
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Compare</Label>
            <Select
              value={compareMode}
              onValueChange={setCompareMode}
              disabled={!startDate || !endDate}
            >
              <SelectTrigger
                className="h-9 w-[190px]"
                title={!startDate || !endDate ? 'Set a date range (or pick a preset) to compare' : undefined}
              >
                <SelectValue placeholder="No comparison" />
              </SelectTrigger>
              <SelectContent>
                {COMPARE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Granularity</Label>
            <Select
              value={granularity || 'auto'}
              onValueChange={(v) => setGranularity(v === 'auto' ? '' : v)}
            >
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">
                  Auto ({GRANULARITY_OPTIONS.find((o) => o.value === effectiveGranularity)?.label})
                </SelectItem>
                {GRANULARITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto self-center text-right">
            <p className="text-token-sm text-muted-foreground">Filters apply automatically</p>
            {pacing?.data_as_of && (
              <p className="text-token-xs text-muted-foreground">
                Data to {pacing.data_as_of.replace('T', ' ').replace('Z', ' UTC')}
                {pacing.pipeline_last_run
                  ? ` · pipeline ran ${pacing.pipeline_last_run.replace('T', ' ').replace('Z', ' UTC')}`
                  : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs (press 1-5 to switch) */}
      <Tabs
        value={String(activeTab)}
        onValueChange={(v) => setActiveTab(Number(v))}
        className="mb-4"
      >
        <TabsList>
          {TAB_LABELS.map((label, i) => (
            <TabsTrigger key={label} value={String(i)}>
              {label}
              <kbd className="rounded border border-borderGrey bg-neutral-50 px-1 text-[10px] font-normal leading-4 text-muted-foreground">
                {i + 1}
              </kbd>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Tab Content */}
      {activeTab === TAB.PACING && (
        <div className="flex w-full flex-col gap-4">
          <MonthStrip
            seasonYear={seasonYear}
            selected={selectedMonth}
            currentMonth={thisMonthKey}
            onSelect={setSelectedMonth}
            onSeasonChange={(y) => {
              setSeasonYear(y);
              // keep the same month-of-season selected across seasons
              if (selectedMonth !== 'season') {
                const mm = selectedMonth.slice(5, 7);
                const yy = Number(mm) >= SEASON_START_MONTH ? y : y + 1;
                setSelectedMonth(`${yy}-${mm}`);
              }
            }}
            minSeasonYear={2024}
            maxSeasonYear={currentSeasonYear + 1}
          />
          {selectedMonth === 'season' ? (
            <SeasonView
              forecast={seasonForecast}
              loading={pacingLoading}
              horizon={forecastHorizon}
              onHorizonChange={setForecastHorizon}
              scenario={scenario}
              onScenarioChange={setScenario}
            />
          ) : (
            <>
              <PacingPanel
                pacing={pacing}
                forecastMonth={monthForecast?.months?.[0]}
                loading={pacingLoading || loading}
                align={pacingAlign}
                onAlignChange={setPacingAlign}
              />
              {selectedMonth <= thisMonthKey && (
                <UpcomingPayments upcoming={upcoming} loading={pacingLoading || loading} />
              )}
            </>
          )}
        </div>
      )}

      {activeTab === TAB.OVERVIEW && (
        <div className="flex w-full flex-col gap-4">
          {/* Summary Cards - calculated from filtered data */}
          <SummaryCards
            timeSeries={timeSeries}
            subscriberMetrics={subscriberMetrics}
            comparison={comparison}
            loading={loading}
            granularity={effectiveGranularity}
          />

          {/* Revenue Chart - full width (has date x-axis) */}
          <RevenueChart
            data={timeSeries}
            comparisonData={comparison?.timeSeries}
            comparisonLabel={comparison?.label}
          />

          {/* Subscriber Chart - full width (has date x-axis) */}
          <SubscriberChart data={subscriberMetrics} />

          {/* Breakdown Charts - manages own grid internally */}
          <BreakdownCharts breakdown={breakdown} timeSeries={timeSeries} />
        </div>
      )}

      {activeTab === TAB.ORDERS && (
        <OrdersTable
          orders={orders}
          page={ordersPage}
          onPageChange={(newPage) => fetchOrders(newPage)}
          pageSize={ordersPageSize}
          onPageSizeChange={handleOrdersPageSizeChange}
          loading={loading}
        />
      )}

      {activeTab === TAB.CUSTOMERS && (
        <CustomersTable customers={customers} loading={loading} />
      )}

      {activeTab === TAB.CHURN && (
        <ChurnTable churn={churn} loading={loading} />
      )}

      {activeTab === TAB.SAAS && (
        <div className="flex w-full flex-col gap-4">
          {/* Advanced SaaS Metrics Summary - always show boxes */}
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <SaasMetricCard
              label="Current MRR"
              accent="#1072CA"
              value={advancedMetrics ? formatCurrency(advancedMetrics.current_mrr || 0, { whole: true }) : '—'}
            />
            <SaasMetricCard
              label="NRR"
              accent={advancedMetrics ? (nrrPositive ? '#59A14F' : '#E15759') : '#9e9e9e'}
              valueClassName={advancedMetrics ? (nrrPositive ? 'text-success' : 'text-destructive') : 'text-muted-foreground'}
              value={advancedMetrics ? formatPercent(advancedMetrics.nrr || 0) : '—'}
            />
            <SaasMetricCard
              label="Avg LTV"
              accent="#B07AA1"
              value={advancedMetrics ? formatCurrency(advancedMetrics.avg_ltv || 0, { whole: true }) : '—'}
            />
            <SaasMetricCard
              label="ARPU"
              accent="#F28E2B"
              value={advancedMetrics ? formatCurrency(advancedMetrics.avg_arpu || 0) : '—'}
            />
          </div>

          {/* MRR Waterfall Chart */}
          <MRRWaterfallChart data={mrrWaterfall} loading={loading && !mrrWaterfall} />

          {/* Cohort Retention Table */}
          <CohortRetentionTable data={cohorts} maxPeriods={12} loading={loading && !cohorts} />
        </div>
      )}
    </div>
  );
}
